import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { User, FileText, Shield, ArrowLeft, UsersIcon, ShieldCheck, Star, X } from "lucide-react";
import Images from "../../../utils/Images";
import StepProgress from "../../../components/auth/StepProgress";
import PersonalContactStep from "../../../components/agentSignup/PersonalContactStep";
import DocumentUploadStep from "../../../components/agentSignup/DocumentUploadStep";
import SecuritySetupStep from "../../../components/agentSignup/SecuritySetupStep";
import { Link, useNavigate } from "react-router-dom";
import authService from "../../../Services/AuthService";

export default function AgentSignUp({ type }) {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    phone_number_api: "",
    gender: "",
    age: "",
    region: "",
    location: "",
    custom_location: "",
    license_prefix: "",
    license_middle: "",
    license_suffix: "",
    id_card_front: null,
    idFrontPreview: "",
    id_card_back: null,
    idBackPreview: "",
    document_type: "",
    card_number: "",
    ghana_card_prefix: "",
    ghana_card_middle: "",
    ghana_card_suffix: "",
    issue_date: "",
    expiry_date: "",
    password: "",
    password_confirmation: "",
    acceptedTerms: false,
  });

  const isRider = type === "rider" || type === "driver" || type === "riders";

  const steps = useMemo(
    () => [
      {
        id: 1,
        title: "Personal & Contact",
        subtitle: "Basic Information",
        icon: User,
        completed: currentStep > 1,
      },
      {
        id: 2,
        title: "Document Upload",
        subtitle: "Identification",
        icon: FileText,
        completed: currentStep > 2,
      },
      {
        id: 3,
        title: "Security Setup",
        subtitle: "Secure your account",
        icon: Shield,
        completed: currentStep > 3,
      },
    ],
    [currentStep]
  );

  const onChange = (field, value) =>
    setFormData((p) => ({ ...p, [field]: value }));

  // Validate Ghana phone number format
  const isValidGhanaPhone = (value) => {
    if (!value) return false;
    
    const digits = value.replace(/\D/g, '');
    
    // Ghana phone numbers: 10 digits starting with 0, or 12 digits starting with 233
    if (digits.startsWith('0') && digits.length === 10) {
      return true;
    }
    if (digits.startsWith('233') && digits.length === 12) {
      return true;
    }
    
    return false;
  };

  // Validate license plate format
  const isValidLicensePlate = () => {
    const prefix = formData.license_prefix || '';
    const middle = formData.license_middle || '';
    const suffix = formData.license_suffix || '';
    
    // Must have prefix (2 letters), middle (1-4 digits), and suffix (2 digits)
    return prefix.length === 2 && 
           middle.length >= 1 && middle.length <= 4 && 
           suffix.length === 2;
  };

  // Get full license plate for API
  const getFullLicensePlate = () => {
    const prefix = formData.license_prefix || '';
    const middle = formData.license_middle || '';
    const suffix = formData.license_suffix || '';
    
    if (!prefix && !middle && !suffix) return '';
    
    let fullPlate = prefix;
    if (middle) fullPlate += '-' + middle;
    if (suffix) fullPlate += '-' + suffix;
    
    return fullPlate;
  };

  // Get full Ghana Card number for API (without hyphens)
  const getFullGhanaCardNumber = () => {
    const prefix = formData.ghana_card_prefix || '';
    const middle = formData.ghana_card_middle || '';
    const suffix = formData.ghana_card_suffix || '';
    
    if (!prefix && !middle && !suffix) return '';
    
    // Combine without hyphens for API payload
    return prefix + middle + suffix;
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        const hasLocation = formData.location && (
          formData.location !== 'Other' || 
          (formData.location === 'Other' && formData.custom_location)
        );
        
        return (
          formData.first_name &&
          formData.last_name &&
          formData.email &&
          formData.phone_number &&
          isValidGhanaPhone(formData.phone_number) &&
          formData.gender &&
          formData.age &&
          formData.region &&
          hasLocation &&
          (isRider ? isValidLicensePlate() : true)
        );
      case 2:
        return true; // Document step is optional
      case 3:
        return (
          formData.password &&
          formData.password_confirmation &&
          formData.password === formData.password_confirmation &&
          formData.password.length >= 6 &&
          formData.acceptedTerms
        );
      default:
        return false;
    }
  };

  const convertFileToBase64 = async (file) => {
    if (!file) return null;
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Ensure phone number is in 233 format for API
  const formatPhoneForAPI = (phoneNumber) => {
    if (!phoneNumber) return phoneNumber;
    
    // Remove all non-digits
    const digits = phoneNumber.replace(/\D/g, '');
    
    // If it starts with 233, keep as is
    if (digits.startsWith('233')) {
      return digits;
    }
    
    // If it starts with 0, replace with 233
    if (digits.startsWith('0')) {
      return '233' + digits.substring(1);
    }
    
    // If it doesn't start with 0 or 233, assume it's a local number and add 233
    if (digits.length > 0 && !digits.startsWith('233') && !digits.startsWith('0')) {
      return '233' + digits;
    }
    
    return digits;
  };

  const handleRegistration = async () => {
    setIsLoading(true);
    setError("");

    try {
      // Convert files to base64 if they exist
      const idCardFrontBase64 = formData.id_card_front ? await convertFileToBase64(formData.id_card_front) : null;
      const idCardBackBase64 = formData.id_card_back ? await convertFileToBase64(formData.id_card_back) : null;

      // Prepare payload according to API specification
      const payload = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone_number: formData.phone_number_api || formatPhoneForAPI(formData.phone_number),
        gender: formData.gender,
        age: formData.age,
        region: formData.region,
        location: formData.location === 'Other' ? formData.custom_location : formData.location,
        password: formData.password,
        password_confirmation: formData.password_confirmation,
        agent_type: type === "rider" || type === "driver" || type === "riders" ? "rider" : type,
      };

      // Add optional fields only if they have values
      const fullLicensePlate = getFullLicensePlate();
      if (fullLicensePlate) {
        payload.license_plate_number = fullLicensePlate;
      }
      if (formData.document_type) {
        payload.document_type = formData.document_type;
      }
      
      // Use appropriate card number based on document type
      if (formData.document_type === 'ghana_card') {
        const ghanaCardNumber = getFullGhanaCardNumber();
        if (ghanaCardNumber) {
          payload.card_number = ghanaCardNumber;
        }
      } else if (formData.card_number) {
        payload.card_number = formData.card_number;
      }
      if (formData.issue_date) {
        payload.issue_date = formData.issue_date;
      }
      if (formData.expiry_date) {
        payload.expiry_date = formData.expiry_date;
      }
      if (idCardFrontBase64) {
        payload.id_card_front = idCardFrontBase64;
      }
      if (idCardBackBase64) {
        payload.id_card_back = idCardBackBase64;
      }

      const response = await authService.register(payload);
      console.log('Registration response:', response);

      if (response.status) {
        // Registration successful, navigate to verification page
        const slug = response.data?.data?.data?.slug;
        
        
        navigate("/verify", { 
          state: { 
            email: formData.email,
            phone: formData.phone_number_api || formatPhoneForAPI(formData.phone_number),
            slug: slug
          } 
        });
      } else {
        setError(response.message || "Registration failed. Please try again.");
      }
    } catch (error) {
      setError("An unexpected error occurred. Please try again.");
      console.error("Registration error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const next = () => {
    if (currentStep < 3) {
      setCurrentStep((s) => s + 1);
    } else {
      handleRegistration();
    }
  };
  const backStep = () => currentStep > 1 && setCurrentStep((s) => s - 1);

  return (
    <div className="grid grid-cols-1 min-h-screen lg:grid-cols-2 lg:h-screen lg:overflow-hidden">
      <motion.div
        className="hidden relative w-full h-full lg:block"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <img
          src={`https://images.unsplash.com/photo-1560264418-c4445382edbc?q=80&w=1470&auto=format&fit=crop`}
          alt="trip-secure-choose-role"
          className="object-cover w-full h-full"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black/80" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/30" />
        <div className="flex absolute inset-0 flex-col justify-between p-12">
          <div>
            <img src={Images.logo_navbar} alt="TripSecure" className="h-10" />
          </div>
          <div className="max-w-xl">
            <div className="p-6 rounded-2xl border border-white/10 bg-white/10 backdrop-blur-md shadow-[0_8px_30px_rgba(0,0,0,0.2)]">
              <motion.h2
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="mb-2 text-2xl font-extrabold text-white"
              >
                Grow with TripSecure Agents
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.5 }}
                className="mb-4 text-sm text-white/80"
              >
                Earn commissions, onboard drivers and riders, and manage your portfolio—all in one dashboard.
              </motion.p>
              <div className="grid grid-cols-3 gap-3">
                <div className="flex gap-2 items-center p-3 rounded-xl border border-white/10 bg-white/10">
                  <UsersIcon size={16} className="text-emerald-300" />
                  <span className="text-xs text-white/90">1k+ agents</span>
                </div>
                <div className="flex gap-2 items-center p-3 rounded-xl border border-white/10 bg-white/10">
                  <ShieldCheck size={16} className="text-emerald-300" />
                  <span className="text-xs text-white/90">Secure payouts</span>
                </div>
                <div className="flex gap-2 items-center p-3 rounded-xl border border-white/10 bg-white/10">
                  <Star size={16} className="text-emerald-300" />
                  <span className="text-xs text-white/90">Trusted by fleets</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
      <button onClick={() => navigate(-1)} className="flex absolute top-4 left-4 z-10 gap-2 items-center px-3 py-2 text-xs font-medium rounded-lg border backdrop-blur text-white/90 border-white/20 bg-white/10 hover:bg-white/20">
        <ArrowLeft size={14} /> Back
      </button>

      <div className="flex justify-center items-start p-6 h-auto lg:p-12 lg:h-screen lg:overflow-y-auto">
        <div className="w-full max-w-xl">
          <div className="flex justify-between items-center mb-6">
            <button
              onClick={() => navigate("/signup")}
              className="flex gap-2 items-center text-sm text-gray-600 hover:text-gray-800"
            >
              <ArrowLeft size={16} /> Back to roles
            </button>
            <img src={Images.logo_colored} alt="TripSecure" className="h-10" />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-2xl font-bold text-center">
              Create{" "}
              {isRider
                ? "Rider"
                : type === "general"
                ? "General Agent"
                : "Direct Agent"}{" "}
              Account
            </h1>
            <p className="mt-1 text-sm text-center text-gray-600">
              Provide your details to get started
            </p>
          </motion.div>

          <StepProgress steps={steps} currentStep={currentStep} />

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="relative p-3 mb-4 text-sm text-red-700 bg-red-100 border border-red-300 rounded-lg"
            >
              <div className="pr-8">
                {error}
              </div>
              <button
                onClick={() => setError("")}
                className="absolute top-2 right-2 p-1 text-red-500 hover:text-red-700 hover:bg-red-200 rounded-full transition-colors"
                aria-label="Close error message"
              >
                <X size={16} />
              </button>
            </motion.div>
          )}

          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {currentStep === 1 && (
                <PersonalContactStep
                  formData={formData}
                  onChange={onChange}
                  showVehicleFields={isRider}
                />
              )}
              {currentStep === 2 && (
                <DocumentUploadStep formData={formData} onChange={onChange} />
              )}
              {currentStep === 3 && (
                <SecuritySetupStep formData={formData} onChange={onChange} termsPath="/terms-of-service" />
              )}
            </motion.div>
          </AnimatePresence>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="mt-6 space-y-4"
          >
            <motion.button
              onClick={next}
              disabled={!isStepValid() || isLoading}
              whileHover={{ scale: isStepValid() && !isLoading ? 1.02 : 1 }}
              whileTap={{ scale: isStepValid() && !isLoading ? 0.98 : 1 }}
              className="overflow-hidden relative px-4 py-3 w-full text-sm font-semibold text-white bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl shadow-lg transition-all duration-300 group hover:from-emerald-600 hover:to-emerald-700 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-emerald-400/50 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="relative z-10 flex items-center justify-center gap-2">
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Creating Account...
                  </>
                ) : (
                  currentStep === 3 ? "Create Account" : "Continue"
                )}
              </span>
              <div className="absolute inset-0 bg-gradient-to-r transition-transform duration-700 -translate-x-full from-white/0 via-white/20 to-white/0 group-hover:translate-x-full"></div>
            </motion.button>
            {currentStep > 1 && (
              <motion.button
                onClick={backStep}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="px-4 py-3 w-full text-sm font-medium text-gray-600 bg-gray-100 rounded-xl transition-colors hover:bg-gray-200"
              >
                Back
              </motion.button>
            )}
            <p className="text-xs text-center text-gray-600">
              Already have an account?{" "}
              <Link
                to="/signin"
                className="text-emerald-600 transition-colors hover:text-emerald-700"
              >
                Login
              </Link>
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
